import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { groupBy, process } from "@progress/kendo-data-query";
import { Input } from "@progress/kendo-react-inputs";
import { Popup } from "@progress/kendo-react-popup";
import { icon, label, placeholder } from "../../../../config";
import "../../../../editor/editor.scss";
import { replaceSpanNode } from "../../../../editor/editorUtils/editorInsertUtils";
import { EMAIL_FUNCTION_CHIPS, INITIAL_VIEW_POPUP_STATE, VIEW_POPUP_TYPES } from "./EmailFlowConstants";

/**
 * Email Chips Popup Component
 * Displays email chips suggestions when @ symbol is typed in the editor
 * @param {Object} props
 * @param {Object} props.viewPopUps - The popup state object
 * @param {Function} props.setViewPopUps - Function to update popup state
 */
const EmailChipsPopup = ({ viewPopUps, setViewPopUps, availableFields, editorRef }) => {
  const [filteredFields, setFilteredFields] = useState(groupBy(availableFields, [{ field: "GROUP" }]));
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPopupType, setCurrentPopupType] = useState(VIEW_POPUP_TYPES.CHIPS);
  const { defaultDetails } = useSelector((state) => state.tasks);
 const [filteredAssigneeList, setFilteredAssigneeList] = useState(defaultDetails?.assigneeList);
  const popupShellRef = useRef(null);


  /**
   * Get the header subtitle based on the current popup type
   * @returns {string} The header subtitle
   * @author Sejal Bansal
   */
  const headerSubtitle = useMemo(() => {
    switch (currentPopupType) {
      case VIEW_POPUP_TYPES.CHIPS:
        return label.TYPE_TILDE_TO_OPEN;
      case VIEW_POPUP_TYPES.ACTIVE_MEMBERS:
        return label.TYPE_AT_SYMBOL_TO_OPEN;
      case VIEW_POPUP_TYPES.FUNCTIONS:
        return label.TYPE_HASH_TO_OPEN;
      default:
        return "";
    }
  }, [currentPopupType]);

  /**
   * Reset the current popup type when the popup opens or anchors to a new trigger (tempId).
   * @author Sejal Bansal
   */
  useEffect(() => {
    if (!viewPopUps.visible) return;
    if (viewPopUps.defaultSection) {
      setCurrentPopupType(viewPopUps.defaultSection);
    }
  }, [viewPopUps.visible, viewPopUps.defaultSection, viewPopUps.tempId]);

/**
 * Reset lists only when the popup opens or anchors to a new trigger (tempId).
 * @author Sejal Bansal
 */
  useEffect(() => {
    if (!viewPopUps.visible) return;
    setSearchQuery("");
    setFilteredFields(groupBy(availableFields, [{ field: "GROUP" }]));
    setFilteredAssigneeList(defaultDetails?.assigneeList ?? []);
  }, [viewPopUps.visible, viewPopUps.tempId]);


  /**
   * Chips / Active Members use a search Input with autoFocus, so Escape hits onKeyDownCapture on this shell.
   * Functions has no input — focus often stays in the editor, outside the portaled popup DOM, so Esc never reaches us.
   * Move focus to the shell (tabIndex -1) when Functions is shown so Escape closes the popup consistently.
   */
  useLayoutEffect(() => {
    if (!viewPopUps.visible || currentPopupType !== VIEW_POPUP_TYPES.FUNCTIONS) return;
    popupShellRef.current?.focus({ preventScroll: true });
  }, [viewPopUps.visible, currentPopupType, viewPopUps.tempId]);

  const closePopupRef = useRef(() => {});
  closePopupRef.current = () => setViewPopUps(INITIAL_VIEW_POPUP_STATE);

  /** React capture handler (no document listeners); covers focus inside search inputs etc. */
  const onPopupKeyDownCapture = (e) => {
    if (e.key !== "Escape") return;
    e.preventDefault();
    e.stopPropagation();
    closePopupRef.current();
  };

  /**
   * Handle popup close
   * @author Sejal Bansal
   */
  const handleClose = () => {
    closePopupRef.current();
  };

  const handlePopupTypeChange = (newType) => {
    setCurrentPopupType(newType);
  };

  /**
   * Handle chip click
   * @param {Object} field - The field object
   */
  const handleChipClick = (field) => {
    const popupSnapshot = { ...viewPopUps };
    replaceSpanNode(editorRef, field?.textInfo, field?.chipId, "email-chip", popupSnapshot);
    handleClose();
  };

  const handleSearch = (e) => {
    const result = process(availableFields, {
      filter: [{ field: "name", operator: "contains", value: e.target.value }],
      group: [{ field: "GROUP" }],
    });
    setFilteredFields(result?.data || []);
    setSearchQuery(e.target.value);
  };

  /**
   * Handle member chip click
   * @param {Object} member - The member object
   */
  const handleMemberChipClick = (member) => {
    handleChipClick({
      member,
      GROUP_TYPE: "memberChip",
      chipId: `memberChip_${member?.label?.replace(/\s+/g, "")}_${member?.id || member?._id || "no-id"}`,
      textInfo: `${member?.label || member?.name || member?.Email}`,
    });
  };

  const handleSearchAssigneeList = (e) => {
    const result = process(defaultDetails?.assigneeList, {
      filter: [
        { field: "label", operator: "contains", value: e.target.value },
        { field: "Email", operator: "contains", value: e.target.value },
      ],
    });
    setFilteredAssigneeList(result?.data || []);
    setSearchQuery(e.target.value);
  };

  return (
    <Popup
      show={viewPopUps.visible}
      anchor={viewPopUps.ref}
      anchorAlign={{ horizontal: "right", vertical: "center" }}
      popupAlign={{ horizontal: "left", vertical: "center" }}
      onClose={handleClose}
      className='email-chips-popup'>
      {viewPopUps.type === VIEW_POPUP_TYPES.EMAIL_CHIPS && (
        <div
          ref={popupShellRef}
          tabIndex={-1}
          className='dt-editor-menu email-chip-container'
          onKeyDownCapture={onPopupKeyDownCapture}>
          <div className='d-flex justify-content-between align-items-center p-2 border-bottom'>
            <div className='line-height-normal'>
              <div className='font-weight-bold line-height-normal'>
                {currentPopupType === VIEW_POPUP_TYPES.CHIPS ? "Chips" : currentPopupType}
              </div>
              <span className='small text-muted line-height-normal'>{headerSubtitle}</span>
            </div>
            <div className='d-flex align-items-center'>
              <span className='small text-muted mr-2'>{label.ESC_PRESS}</span>
              <span className='square-icon square-icon-medium cursor-pointer' onClick={handleClose} title={label.CLOSE_POPUP}>
                {icon.CLOSE}
              </span>
            </div>
          </div>

          <div className='d-flex justify-content-around align-items-center p-2 border-bottom'>
            <span
              className={`cursor-pointer d-flex align-items-center justify-content-center smart-chip-tab-icon app-icon-medium ${
                currentPopupType === VIEW_POPUP_TYPES.CHIPS ? "active" : ""
              }`}
              onClick={() => handlePopupTypeChange(VIEW_POPUP_TYPES.CHIPS)}
              title='Chips'>
              {icon.MICROCHIP}
            </span>
            <span
              className={`cursor-pointer d-flex align-items-center justify-content-center smart-chip-tab-icon app-icon-medium ${
                currentPopupType === VIEW_POPUP_TYPES.ACTIVE_MEMBERS ? "active" : ""
              }`}
              onClick={() => handlePopupTypeChange(VIEW_POPUP_TYPES.ACTIVE_MEMBERS)}
              title={VIEW_POPUP_TYPES.ACTIVE_MEMBERS}>
              {icon.PERSON_SINGLE}
            </span>
            <span
              className={`cursor-pointer d-flex align-items-center justify-content-center smart-chip-tab-icon app-icon-medium ${
                currentPopupType === VIEW_POPUP_TYPES.FUNCTIONS ? "active" : ""
              }`}
              onClick={() => handlePopupTypeChange(VIEW_POPUP_TYPES.FUNCTIONS)}
              title={VIEW_POPUP_TYPES.FUNCTIONS}>
              {icon.SQUARE_FUNCTION}
            </span>
          </div>

          <div className='custom-editor-section'>
            {currentPopupType === VIEW_POPUP_TYPES.CHIPS && (
              <>
                <div className='smart-chip-search-container'>
                  <span className='smart-chip-search-icon'>{icon.SEARCH}</span>
                  <div className='smart-chip-search-input'>
                    <Input placeholder={placeholder.SEARCH} value={searchQuery} onChange={handleSearch} clearButton={true} autoFocus={true}/>
                  </div>
                </div>
                <div className='email-list-container dt-editor-users overflow-auto overflow-x-hidden w-100'>
                  {filteredFields?.map((field) => (
                    <div key={field.value}>
                      <div className='font-weight-bold small px-3 pt-1 pb-1 '>{field?.value}</div>
                      <div>
                        {field?.items?.map((item) => (
                          <div key={item?.id} className='email-list-item cursor-pointer' onClick={() => handleChipClick(item)}>
                            <span className='text-size'>{item?.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {currentPopupType === VIEW_POPUP_TYPES.ACTIVE_MEMBERS && (
              <>
                <div className='smart-chip-search-container'>
                  <span className='smart-chip-search-icon'>{icon.SEARCH}</span>
                  <div className='smart-chip-search-input'>
                    <Input placeholder={placeholder.SEARCH} value={searchQuery} onChange={handleSearchAssigneeList} clearButton={true} autoFocus={true}/>
                  </div>
                </div>
                <div className='email-list-container dt-editor-users overflow-auto overflow-x-hidden w-100'>
                  {filteredAssigneeList.map((member) => (
                    <div
                      key={member.id}
                      className='d-flex align-items-center cursor-pointer dt-editor-users-list'
                      onClick={() => handleMemberChipClick(member)}>
                      {member?.PhotoLinks?.small ? (
                        <img src={member?.PhotoLinks?.small} className='mr-2 userlist-dp rounded-circle' alt='' />
                      ) : (
                        <div className='userlist-icon'>{icon.ACCOUNT_CIRCLE}</div>
                      )}
                      <div>
                        <div className='small assignee-info d-block'>{member.label}</div>
                        <div className='text-muted small info-text'>{member.Email}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {currentPopupType === VIEW_POPUP_TYPES.FUNCTIONS && (
              <div className='email-list-container dt-editor-users overflow-auto overflow-x-hidden w-100'>
                {EMAIL_FUNCTION_CHIPS.map((item) => (
                  <div key={item.value}>
                    <div className='font-weight-bold small px-3 pt-2 pb-1 text-muted'>{item.label}</div>
                    <div>
                      <div className='email-list-item cursor-pointer' onClick={() => handleChipClick(item)}>
                        <span className='text-size'>{item.value}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Popup>
  );
};

export default EmailChipsPopup;
